import React, { useState, useEffect, useCallback, useMemo } from 'react';
import FlightResultCard from '../flights/FlightResultCard';
import SmartSavingsCard from '../flights/SmartSavingsCard';
import StrategyOptimizedCard from '../flights/StrategyOptimizedCard';
import FilterChips from '../ui/FilterChips';
import LoadingSpinner from '../ui/LoadingSpinner';
import MaterialIcon from '../ui/MaterialIcon';
import { searchFlights } from '../../services/flightApi';
import { calculateSmartSavings } from '../../services/smartSavings';
import { useLoyalty } from '../../contexts/LoyaltyContext';
import transferPartners from '../../data/transferPartners';

const FILTER_CHIPS = [
  { id: 'cheapest', label: 'Cheapest' },
  { id: 'fastest', label: 'Fastest' },
  { id: 'one-stop', label: '1 Stop' },
  { id: 'nonstop', label: 'Nonstop' },
  { id: 'airlines', label: 'Airlines' },
];

function FlightResultsScreen({ searchData, onBack, onSelectStrategy, cards, benefitsData }) {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('cheapest');
  const [selectedFlightId, setSelectedFlightId] = useState(null);
  const { programs: loyaltyPrograms } = useLoyalty();

  const fetchFlights = useCallback(async () => {
    setLoading(true);
    try {
      const results = await searchFlights(searchData);
      setFlights(results);
    } catch (error) {
      console.error('Failed to fetch flights:', error);
    } finally {
      setLoading(false);
    }
  }, [searchData]);

  useEffect(() => {
    fetchFlights();
  }, [fetchFlights]);

  // Pre-compute savings for all flights
  const savingsMap = useMemo(() => {
    const map = {};
    for (const flight of flights) {
      const result = calculateSmartSavings(flight, cards || [], loyaltyPrograms, benefitsData, transferPartners);
      map[flight.id] = result;
    }
    return map;
  }, [flights, cards, loyaltyPrograms, benefitsData]);

  // Build strategy card from best savings across all flights
  const topStrategy = useMemo(() => {
    if (flights.length === 0 || !cards || cards.length === 0) return null;
    const cheapest = flights.reduce((min, f) => (f.price < min.price ? f : min), flights[0]);
    const savings = savingsMap[cheapest.id];
    if (!savings || savings.totalSavings <= 0) return null;
    const bestStrat = savings.strategies[0];
    if (!bestStrat) return null;
    return {
      cardName: savings.bestCard?.cardName || bestStrat.label,
      cardNetwork: 'Points',
      pointsMultiplier: savings.bestCard?.earnRate || '',
      estimatedSavings: Math.round(savings.totalSavings),
      explanation: bestStrat.detail,
      savingsData: savings,
      flight: cheapest,
    };
  }, [flights, cards, savingsMap]);

  const filteredFlights = useMemo(() => {
    const sorted = [...flights];
    switch (activeFilter) {
      case 'cheapest':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'fastest':
        sorted.sort((a, b) => {
          const durationToMin = (d) => {
            const parts = d.match(/(\d+)h\s*(\d+)m/);
            return parts ? parseInt(parts[1]) * 60 + parseInt(parts[2]) : 9999;
          };
          return durationToMin(a.duration) - durationToMin(b.duration);
        });
        break;
      case 'one-stop':
        return sorted.filter((f) => f.stops === 1);
      case 'nonstop':
        return sorted.filter((f) => f.stops === 0);
      default:
        break;
    }
    return sorted;
  }, [flights, activeFilter]);

  const handleFlightSelect = useCallback((flight) => {
    setSelectedFlightId((prev) => (prev === flight.id ? null : flight.id));
  }, []);

  const handleViewStrategy = useCallback((savings, flight) => {
    if (onSelectStrategy) {
      onSelectStrategy({
        ...savings,
        flight,
        cardName: savings.bestCard?.cardName,
      });
    }
  }, [onSelectStrategy]);

  const origin = searchData?.origin || 'JFK';
  const destination = searchData?.destination || 'DXB';
  const originCity = searchData?.originCity || '';
  const destinationCity = searchData?.destinationCity || '';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="w-10 h-10 rounded-full bg-surface-container flex items-center justify-center hover:bg-surface-container-high transition-colors"
        >
          <MaterialIcon name="arrow_back" size={20} className="text-on-surface" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div>
              <span className="text-lg font-bold text-on-surface">{origin}</span>
              {originCity && <span className="text-xs text-on-surface-variant ml-1">{originCity}</span>}
            </div>
            <MaterialIcon name="arrow_forward" size={18} className="text-primary" />
            <div>
              <span className="text-lg font-bold text-on-surface">{destination}</span>
              {destinationCity && <span className="text-xs text-on-surface-variant ml-1">{destinationCity}</span>}
            </div>
          </div>
          <p className="text-xs text-on-surface-variant mt-0.5">
            {searchData?.departureDate || 'Apr 15'}
            {searchData?.tripType !== 'one-way' && searchData?.returnDate ? ` — ${searchData.returnDate}` : ''}
            {' '}&middot; {searchData?.cabin || 'Economy'} &middot;{' '}
            {(searchData?.adults || 1) + (searchData?.children || 0)} passenger(s)
            {searchData?.tripType === 'one-way' ? ' · One Way' : ''}
          </p>
        </div>
      </div>

      {/* Strategy Card */}
      {!loading && topStrategy && (
        <StrategyOptimizedCard
          strategy={topStrategy}
          onViewDetails={onSelectStrategy ? () => onSelectStrategy(topStrategy) : undefined}
        />
      )}

      {/* Filter Chips */}
      <FilterChips
        chips={FILTER_CHIPS}
        activeChip={activeFilter}
        onChipClick={setActiveFilter}
      />

      {/* Results */}
      {loading ? (
        <LoadingSpinner text="Searching flights..." size="lg" />
      ) : filteredFlights.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <MaterialIcon name="flight_takeoff" size={48} className="text-outline-variant mb-3" />
          <p className="text-base font-semibold text-on-surface">No flights found</p>
          <p className="text-sm text-on-surface-variant mt-1">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-[11px] text-on-surface-variant/70">
            {filteredFlights.length} flight{filteredFlights.length !== 1 ? 's' : ''} found
          </p>
          {filteredFlights.map((flight) => (
            <div key={flight.id}>
              <FlightResultCard
                flight={flight}
                savings={savingsMap[flight.id]?.totalSavings || 0}
                onSelect={handleFlightSelect}
              />
              {selectedFlightId === flight.id && savingsMap[flight.id] && (
                <div className="mt-2">
                  <SmartSavingsCard
                    savings={savingsMap[flight.id]}
                    onViewStrategy={() => handleViewStrategy(savingsMap[flight.id], flight)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FlightResultsScreen;
